import {ChevronLeftIcon, useUserContext} from "@hosttools/frontend";
import {
    Button,
    ButtonIcon,
    ButtonText,
    Heading,
    HStack,
    Steps,
    Text,
    VStack
} from "@hosttools/frontend/components";
import {useMobileView} from "@hosttools/frontend/hooks/useMobileView";
import type {Listing} from "@hosttools/frontend/models/listing";
import {createColumnHelper} from "@tanstack/react-table";
import type React from "react";
import {memo, useCallback, useMemo} from "react";
import {useHistory} from "react-router-dom";

import ConnectedListingItem from "./ConnectedListingItem";
import ListingChannelsCell from "./ListingChannelsCell";
import ListingImagesCell from "./ListingImagesCell";

import TableFetch from "@/client/components/TableFetch";

type Props = {
    goBack: () => void;
};

const ConnectedListings: React.FC<Props> = ({goBack}) => {
    const history = useHistory();
    const isMobile = useMobileView();
    const {listings} = useUserContext();

    const columnHelper = useMemo(() => createColumnHelper<Listing>(), []);

    const columns = useMemo(
        () => [
            columnHelper.accessor(row => row.name, {
                id: "image",
                header: "Images",
                cell: ListingImagesCell
            }),
            columnHelper.accessor(row => row.name, {
                id: "title",
                header: "Title"
            }),
            columnHelper.accessor(row => row.airbnbAddress, {
                id: "address",
                header: "Address"
            }),
            columnHelper.display({
                id: "channels",
                header: "Channels",
                align: "end",
                cell: ListingChannelsCell
            })
        ],
        [columnHelper]
    );

    const goHome = useCallback(() => {
        history.push("/");
    }, [history]);

    return (
        <VStack className="w-full max-w-[940px] pt-5 md:pt-10 px-5 md:px-1" space="xl">
            <Button
                no110MinWidth
                action="default"
                className="self-start md:hidden"
                variant="ghost"
                onPress={goBack}
            >
                <ButtonIcon as={ChevronLeftIcon} />
                <ButtonText>Back</ButtonText>
            </Button>
            <Steps className="flex md:hidden w-fit" step={3} totalSteps={3} />
            <VStack space="sm">
                <Heading variant={isMobile ? "md" : "2xl"}>Connect Addtional Channel</Heading>
                <Text isParagraph variant={isMobile ? "sm" : "lg"}>
                    Import your listings from one of these channels or manually create a listing on
                    Host Tools.
                </Text>
            </VStack>
            {isMobile ? (
                <VStack space="sm">
                    {listings.map(listing => (
                        <ConnectedListingItem key={listing._id} listing={listing} />
                    ))}
                </VStack>
            ) : (
                <TableFetch
                    columns={columns}
                    data={listings}
                    enableGlobalFilter={false}
                    gridTemplateColumnsClass="grid-cols-[_minmax(min-content,_auto)_minmax(200px,_auto)_minmax(min-content,_auto)_minmax(min-content,_auto)]"
                    mode="offline"
                    rowId="_id"
                    testID="import-listings"
                    variant={isMobile ? "list" : "table"}
                />
            )}
            <HStack className="md:justify-end mt-1" space="sm">
                {!isMobile && (
                    <Button variant="outline" onPress={goBack}>
                        <ButtonText>Back</ButtonText>
                    </Button>
                )}
                <Button className="flex-grow md:flex-grow-0" testID="btn-done" onPress={goHome}>
                    <ButtonText>Done</ButtonText>
                </Button>
            </HStack>
        </VStack>
    );
};

export default memo(ConnectedListings);
